(() => {
  'use strict';

  if (window.VelloriaNavigation) {
    window.VelloriaNavigation.mount(document);
    return;
  }

  const controllers = new Set();
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class HeaderController {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.dialog = root.querySelector('[data-mobile-menu]');
      this.mobileTriggers = Array.from(root.querySelectorAll('[data-mobile-open]'));
      this.searchPanel = root.querySelector('[data-search-panel]');
      this.searchTriggers = Array.from(root.querySelectorAll('[data-search-open]'));
      this.desktopQuery = window.matchMedia('(min-width: 901px)');
      this.returnFocus = null;
      this.restoreMobileFocus = true;
      this.init();
    }

    init() {
      this.root.querySelectorAll('[data-mobile-open]').forEach((button) => {
        button.addEventListener('click', () => this.openMobile(button), { signal: this.signal });
      });

      this.root.querySelectorAll('[data-mobile-close], [data-nav-overlay]').forEach((button) => {
        button.addEventListener('click', () => this.closeMobile(true), { signal: this.signal });
      });

      if (this.dialog) {
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeMobile(true);
        }, { signal: this.signal });
        this.dialog.addEventListener('close', () => this.finalizeMobileClose(), { signal: this.signal });
        this.dialog.addEventListener('keydown', (event) => this.trapDialogFocus(event), { signal: this.signal });
      }

      this.searchTriggers.forEach((button) => {
        button.addEventListener('click', () => this.toggleSearch(button), { signal: this.signal });
      });

      this.root.querySelectorAll('[data-search-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeSearch(true), { signal: this.signal });
      });

      this.root.querySelectorAll('[data-submenu-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggleSubmenu(button), { signal: this.signal });
      });

      document.addEventListener('click', (event) => this.handleOutsideClick(event), { signal: this.signal });
      document.addEventListener('keydown', (event) => this.handleDocumentKeydown(event), { signal: this.signal });
      this.desktopQuery.addEventListener('change', (event) => {
        if (event.matches) this.closeMobile(false);
      }, { signal: this.signal });
    }

    openMobile(trigger) {
      if (!this.dialog) return;
      this.closeSearch(false);
      this.closeSubmenus();
      this.returnFocus = trigger;
      this.restoreMobileFocus = true;
      this.dialog.hidden = false;
      this.dialog.removeAttribute('inert');
      this.dialog.setAttribute('aria-hidden', 'false');
      this.mobileTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('v-nav-locked');

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      requestAnimationFrame(() => {
        const firstTarget = this.dialog.querySelector('[data-mobile-close]') || this.dialog.querySelector(focusableSelector);
        if (firstTarget) firstTarget.focus();
      });
    }

    closeMobile(restoreFocus) {
      if (!this.dialog || this.dialog.hidden) return;
      this.restoreMobileFocus = restoreFocus;
      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.finalizeMobileClose();
      }
    }

    finalizeMobileClose() {
      if (!this.dialog) return;
      this.dialog.setAttribute('aria-hidden', 'true');
      this.dialog.setAttribute('inert', '');
      this.dialog.hidden = true;
      this.mobileTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('v-nav-locked');
      if (this.restoreMobileFocus && this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus();
      this.returnFocus = null;
    }

    trapDialogFocus(event) {
      if (event.key !== 'Tab' || !this.dialog || this.dialog.hidden) return;
      const focusable = Array.from(this.dialog.querySelectorAll(focusableSelector)).filter((element) => {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
      });
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    toggleSearch(trigger) {
      if (!this.searchPanel) return;
      if (this.searchPanel.hidden) {
        this.closeSubmenus();
        this.searchPanel.hidden = false;
        this.searchPanel.removeAttribute('inert');
        this.searchTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        requestAnimationFrame(() => {
          const input = this.searchPanel.querySelector('input[type="search"]');
          if (input) input.focus();
        });
      } else {
        this.closeSearch(true, trigger);
      }
    }

    closeSearch(restoreFocus, explicitTrigger) {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.searchPanel.setAttribute('inert', '');
      this.searchPanel.hidden = true;
      this.searchTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      const target = explicitTrigger || this.searchTriggers[0];
      if (restoreFocus && target && target.isConnected) target.focus();
    }

    toggleSubmenu(button) {
      const panel = this.root.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
      if (!panel) return;
      const willOpen = panel.hidden;
      this.closeSearch(false);
      this.closeSubmenus(button);
      panel.hidden = !willOpen;
      if (willOpen) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
      button.setAttribute('aria-expanded', String(willOpen));
    }

    closeSubmenus(exceptButton) {
      this.root.querySelectorAll('[data-submenu-toggle]').forEach((button) => {
        if (button === exceptButton) return;
        const panel = this.root.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        button.setAttribute('aria-expanded', 'false');
        if (panel) {
          panel.setAttribute('inert', '');
          panel.hidden = true;
        }
      });
    }

    handleOutsideClick(event) {
      if (!this.root.contains(event.target)) {
        this.closeSearch(false);
        this.closeSubmenus();
        return;
      }

      if (this.searchPanel && !this.searchPanel.hidden && !this.searchPanel.contains(event.target) && !event.target.closest('[data-search-open]')) {
        this.closeSearch(false);
      }

      if (!event.target.closest('[data-submenu]') && !event.target.closest('[data-submenu-toggle]')) {
        this.closeSubmenus();
      }
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.dialog && !this.dialog.hidden) {
        event.preventDefault();
        this.closeMobile(true);
        return;
      }

      if (this.searchPanel && !this.searchPanel.hidden) {
        event.preventDefault();
        this.closeSearch(true);
        return;
      }

      const openButton = this.root.querySelector('[data-submenu-toggle][aria-expanded="true"]');
      if (openButton) {
        event.preventDefault();
        this.closeSubmenus();
        openButton.focus();
      }
    }

    destroy() {
      this.closeMobile(false);
      this.abortController.abort();
      delete this.root.dataset.navigationInitialized;
    }
  }

  class AnnouncementController {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.items = Array.from(root.querySelectorAll('[data-announcement-item]'));
      this.viewport = root.querySelector('[data-announcement-viewport]');
      this.toggleButton = root.querySelector('[data-announcement-toggle]');
      this.intervalDuration = Math.max(Number(root.dataset.interval) || 6000, 4000);
      this.autoplay = root.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = !this.autoplay || this.reducedMotion;
      this.temporarilyPaused = false;
      this.timer = null;
      this.index = Math.max(this.items.findIndex((item) => item.classList.contains('is-active')), 0);
      this.init();
    }

    init() {
      if (this.items.length < 2) return;
      const previousButton = this.root.querySelector('[data-announcement-prev]');
      const nextButton = this.root.querySelector('[data-announcement-next]');
      if (previousButton) previousButton.addEventListener('click', () => this.manualMove(-1), { signal: this.signal });
      if (nextButton) nextButton.addEventListener('click', () => this.manualMove(1), { signal: this.signal });
      if (this.toggleButton) this.toggleButton.addEventListener('click', () => this.toggleAutoplay(), { signal: this.signal });

      this.root.addEventListener('mouseenter', () => {
        this.temporarilyPaused = true;
        this.stop();
      }, { signal: this.signal });
      this.root.addEventListener('mouseleave', () => {
        this.temporarilyPaused = false;
        this.start();
      }, { signal: this.signal });
      this.root.addEventListener('focusin', () => {
        this.temporarilyPaused = true;
        this.stop();
      }, { signal: this.signal });
      this.root.addEventListener('focusout', (event) => {
        if (this.root.contains(event.relatedTarget)) return;
        this.temporarilyPaused = false;
        this.start();
      }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      }, { signal: this.signal });

      this.updateToggle();
      this.start();
    }

    show(index, announce) {
      this.index = (index + this.items.length) % this.items.length;
      this.items.forEach((item, itemIndex) => {
        const active = itemIndex === this.index;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
      });
      if (this.viewport) this.viewport.setAttribute('aria-live', announce ? 'polite' : 'off');
    }

    manualMove(direction) {
      this.userPaused = true;
      this.stop();
      this.show(this.index + direction, true);
      this.updateToggle();
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      if (this.userPaused) this.stop();
      else this.start();
      this.updateToggle();
    }

    updateToggle() {
      if (!this.toggleButton) return;
      const paused = this.userPaused;
      const label = paused ? this.root.dataset.labelPlay : this.root.dataset.labelPause;
      this.toggleButton.setAttribute('aria-label', label);
      this.toggleButton.setAttribute('aria-pressed', String(paused));
      const pauseIcon = this.toggleButton.querySelector('[data-announcement-icon-pause]');
      const playIcon = this.toggleButton.querySelector('[data-announcement-icon-play]');
      if (pauseIcon) pauseIcon.hidden = paused;
      if (playIcon) playIcon.hidden = !paused;
    }

    start() {
      if (this.timer || this.userPaused || this.temporarilyPaused || document.hidden || this.items.length < 2) return;
      if (this.viewport) this.viewport.setAttribute('aria-live', 'off');
      this.timer = window.setInterval(() => this.show(this.index + 1, false), this.intervalDuration);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stop();
      this.abortController.abort();
      delete this.root.dataset.announcementInitialized;
    }
  }

  const mount = (scope) => {
    const headerRoots = [];
    const announcementRoots = [];
    if (scope.matches && scope.matches('[data-navigation-root]')) headerRoots.push(scope);
    if (scope.matches && scope.matches('[data-announcement]')) announcementRoots.push(scope);
    scope.querySelectorAll?.('[data-navigation-root]').forEach((root) => headerRoots.push(root));
    scope.querySelectorAll?.('[data-announcement]').forEach((root) => announcementRoots.push(root));

    headerRoots.forEach((root) => {
      if (root.dataset.navigationInitialized === 'true') return;
      root.dataset.navigationInitialized = 'true';
      controllers.add(new HeaderController(root));
    });

    announcementRoots.forEach((root) => {
      if (root.dataset.announcementInitialized === 'true') return;
      root.dataset.announcementInitialized = 'true';
      controllers.add(new AnnouncementController(root));
    });
  };

  const unmount = (scope) => {
    controllers.forEach((controller) => {
      if (scope === controller.root || scope.contains(controller.root)) {
        controller.destroy();
        controllers.delete(controller);
      }
    });
  };

  window.VelloriaNavigation = { mount, unmount };
  mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();

